// ── Employee Performance widget: data contract, columns, settings and sorting ──
//
// Everything the widget, its column editor and the three dashboard pages share lives here so the
// rules (which columns exist, what a saved layout may contain, how rows sort) are pure and tested.
// Figures come from GET /api/dashboard/employee-performance, which applies the dashboard's store,
// channel, NET/GROSS and multibuy filters exactly as the KPI cards do — so the Sales total here
// equals the Net Sales card for the same view.

import { API_BASE } from './api'

// ── API contract ─────────────────────────────────────────────────────

export interface EmployeePerformanceFigures {
    transactions: number
    units: number
    /** After line discounts — the dashboard's Net Sales (Gross Sales when returns are excluded). */
    netSales: number
    /** Before line discounts: Σ Retail × Qty. */
    grossRetail: number
    /** Σ ItemDiscount × Qty — every line discount, promotional pricing included. */
    discounts: number
    /** Adventure Bucks credited on the transactions this employee rang. */
    bucksIssued: number
    /**
     * Adventure Bucks the employee credited by hand (ad-hoc grants and retroactive credits applied
     * at the till), by when and where they were applied. Null unless the request asked for it
     * (includeManualGrants), so "not asked for" can never pass for $0.00 — and absent from a server
     * older than the column, hence optional.
     */
    manualGrants?: number | null
}

export interface EmployeePerformanceRow extends EmployeePerformanceFigures {
    employeeId: string
    name: string
    /** Legacy store GUID of the employee's own store (not necessarily where the sale rang). */
    homeStoreId: string | null
    homeStoreName: string | null
    /** The employee record is flagged deleted — they have left, but still rang these sales. */
    departed: boolean
}

export interface EmployeePerformanceData {
    /** Inclusive yyyy-MM-dd bounds of the window the server actually queried. */
    startDate: string
    endDate: string
    employees: EmployeePerformanceRow[]
    totals: EmployeePerformanceFigures
}

export type EmployeePerformancePeriod = 'daily' | 'monthly' | 'custom'

export interface EmployeePerformanceFilters {
    storeId: string
    channel: string
    includeReturns: boolean
    includeMultibuys: boolean
    /** daily: the day (yyyy-MM-dd). monthly: any day in the month (yyyy-MM-01). */
    date?: string
    /** custom: inclusive range (yyyy-MM-dd). */
    startDate?: string
    endDate?: string
    /** Ask for the Manual grants figures — a second, costlier query, so only while the column shows. */
    includeManualGrants?: boolean
}

export function buildEmployeePerformanceUrl(period: EmployeePerformancePeriod, f: EmployeePerformanceFilters): string {
    const p = new URLSearchParams({
        period,
        storeId: f.storeId,
        channel: f.channel,
        includeReturns: String(f.includeReturns),
        includeMultibuys: String(f.includeMultibuys),
    })
    if (period === 'custom') {
        if (f.startDate) p.set('startDate', f.startDate)
        if (f.endDate) p.set('endDate', f.endDate)
    } else if (f.date) {
        p.set('date', f.date)
    }
    if (f.includeManualGrants) p.set('includeManualGrants', 'true')
    return `/api/dashboard/employee-performance?${p.toString()}`
}

/** A failed fetch that keeps the HTTP status, so the widget can tell "not allowed" from "broken". */
export class EmployeePerformanceError extends Error {
    readonly status: number
    constructor(status: number, message: string) {
        super(message)
        this.name = 'EmployeePerformanceError'
        this.status = status
    }
}

/** The user-facing sentence for a failed load; never echoes server internals. */
export function describeEmployeePerformanceError(err: unknown): string {
    if (err instanceof EmployeePerformanceError) {
        if (err.status === 403) return err.message || "You don't have access to employee figures for this selection."
        if (err.status === 400) return err.message || 'These filters can’t be shown as employee figures.'
    }
    return 'Employee figures could not be loaded. Try refreshing.'
}

export async function fetchEmployeePerformance(url: string, token: string | null): Promise<EmployeePerformanceData> {
    const r = await fetch(API_BASE + url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
    if (!r.ok) {
        let message = ''
        try {
            const body = await r.json()
            if (body && typeof body.error === 'string') message = body.error
        } catch { /* non-JSON body — keep the generic sentence */ }
        throw new EmployeePerformanceError(r.status, message)
    }
    return r.json()
}

// ── Placement ────────────────────────────────────────────────────────

/**
 * Grid rows the widget takes on a phone (the xs breakpoint). Other widgets get 10 there, but at 10
 * (190px) the sticky header and totals rows leave room for less than one employee; 24 (470px)
 * shows about seven. The layout stores and both grids size it from this.
 */
export const EMPLOYEE_PERFORMANCE_XS_ROWS = 24

// ── Columns ──────────────────────────────────────────────────────────

export type EmployeeColumnKey =
    | 'rank' | 'employee' | 'sales' | 'discounts' | 'bucks' | 'manualGrants'
    | 'discountPct' | 'transactions' | 'avgSale' | 'units' | 'upt' | 'salesShare'

export type EmployeeColumnFormat = 'text' | 'currency' | 'currencyCents' | 'int' | 'decimal' | 'percent'

export interface EmployeeColumnContext {
    /** NET (true) or GROSS (false) — renames the sales column the way the Net Sales card is renamed. */
    includeReturns: boolean
}

export interface EmployeeColumnDef {
    key: EmployeeColumnKey
    /** Short header text. */
    label: (ctx: EmployeeColumnContext) => string
    /** Longer name used in the column editor. */
    menuLabel: (ctx: EmployeeColumnContext) => string
    /** Header tooltip / editor hint: what the number is and how it is worked out. */
    description: string
    numeric: boolean
    format: EmployeeColumnFormat
    /** Shown when a layout has never configured the widget. */
    defaultVisible: boolean
    /** Direction a first click (or the editor's sort toggle) sorts in. */
    defaultDir: SortDir
    sortable: boolean
    /** Can't be hidden (the table is meaningless without it). */
    lockedVisible?: boolean
}

export type SortDir = 'asc' | 'desc'

const salesName = (ctx: EmployeeColumnContext) => (ctx.includeReturns ? 'Net Sales' : 'Gross Sales')

/** Canonical order — also the default display order. */
export const EMPLOYEE_COLUMNS: readonly EmployeeColumnDef[] = [
    {
        key: 'rank', label: () => '#', menuLabel: () => 'Rank',
        description: 'Position in the current sort order.',
        numeric: false, format: 'text', defaultVisible: true, defaultDir: 'asc', sortable: false,
    },
    {
        key: 'employee', label: () => 'Employee', menuLabel: () => 'Employee',
        description: 'The employee who rang the sale.',
        numeric: false, format: 'text', defaultVisible: true, defaultDir: 'asc', sortable: true, lockedVisible: true,
    },
    {
        key: 'sales', label: salesName, menuLabel: salesName,
        description: 'Sales after line discounts, before tax — the same figure as the Net Sales card. With GROSS selected, returns are left out.',
        numeric: true, format: 'currency', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'discounts', label: () => 'Discounts', menuLabel: () => 'Total discounts',
        description: 'Every line discount given on the employee’s sales, promotional pricing included.',
        numeric: true, format: 'currency', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'bucks', label: () => 'Adv. Bucks', menuLabel: () => 'Adventure Bucks issued',
        description: 'Adventure Bucks credited to customers on the sales this employee rang.',
        numeric: true, format: 'currencyCents', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'manualGrants', label: () => 'Manual Grants', menuLabel: () => 'Adventure Bucks granted by hand',
        description: 'Adventure Bucks this employee credited by hand — ad-hoc grants and retroactive credits applied at the till — '
            + 'dated when applied, at the store where they were entered (or the store of the sale, where no store was recorded). '
            + 'Only the date and store filters apply. '
            + 'A retroactive credit also counts under Adv. Bucks for whoever rang the original sale.',
        numeric: true, format: 'currencyCents', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'discountPct', label: () => 'Disc. %', menuLabel: () => 'Discount depth',
        description: 'Discounts as a share of the price before discount: Discounts ÷ (Sales + Discounts).',
        numeric: true, format: 'percent', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'transactions', label: () => 'Txns', menuLabel: () => 'Transactions',
        description: 'Receipts the employee rang.',
        numeric: true, format: 'int', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'avgSale', label: () => 'Avg Sale', menuLabel: () => 'Average sale',
        description: 'Sales ÷ Transactions.',
        numeric: true, format: 'currency', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'units', label: () => 'Units', menuLabel: () => 'Units sold',
        description: 'Items sold; returned items count as negative units when returns are included.',
        numeric: true, format: 'int', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'upt', label: () => 'UPT', menuLabel: () => 'Units per transaction',
        description: 'Units ÷ Transactions.',
        numeric: true, format: 'decimal', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'salesShare', label: () => '% of Sales', menuLabel: () => 'Share of sales',
        description: 'The employee’s share of all sales in this view.',
        numeric: true, format: 'percent', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
]

const COLUMN_BY_KEY = new Map(EMPLOYEE_COLUMNS.map(c => [c.key, c]))

export function getEmployeeColumn(key: EmployeeColumnKey): EmployeeColumnDef {
    return COLUMN_BY_KEY.get(key)!
}

export function isEmployeeColumnKey(key: unknown): key is EmployeeColumnKey {
    return typeof key === 'string' && COLUMN_BY_KEY.has(key as EmployeeColumnKey)
}

// ── Settings (persisted inside the dashboard layout's widgetSettings) ──

export interface EmployeeColumnConfig {
    key: EmployeeColumnKey
    visible: boolean
}

/** The widget's slice of WidgetSettings. Every field is optional: layouts saved before a field
 *  existed must still render, so readers go through {@link resolveEmployeeSettings}. */
export interface EmployeePerformanceSettings {
    /** Display order and visibility. Array order is column order. */
    employeeColumns?: EmployeeColumnConfig[]
    employeeSortKey?: EmployeeColumnKey
    employeeSortDir?: SortDir
    /** Footer row with the totals for every employee in the view. Default on. */
    employeeShowTotals?: boolean
    /** Thin bar under each sales figure, scaled to the top seller. Default on. */
    employeeShowBars?: boolean
    /** Show only the first N rows of the sort; 0 or absent shows everyone. */
    employeeRowLimit?: number
}

export const EMPLOYEE_ROW_LIMITS = [0, 5, 10, 25] as const

export const DEFAULT_EMPLOYEE_SORT: { key: EmployeeColumnKey; dir: SortDir } = { key: 'sales', dir: 'desc' }

export function defaultEmployeeColumns(): EmployeeColumnConfig[] {
    return EMPLOYEE_COLUMNS.map(c => ({ key: c.key, visible: c.defaultVisible }))
}

/**
 * Reconciles a saved column list with the canonical one: unknown and duplicate keys are dropped,
 * columns the saved list has never heard of are appended (with their default visibility), and a
 * column that can't be hidden is forced visible. Always returns a fresh array.
 */
export function normalizeEmployeeColumns(saved: unknown): EmployeeColumnConfig[] {
    if (!Array.isArray(saved) || saved.length === 0) return defaultEmployeeColumns()
    const seen = new Set<EmployeeColumnKey>()
    const out: EmployeeColumnConfig[] = []
    for (const item of saved) {
        const key = (item as { key?: unknown })?.key
        if (!isEmployeeColumnKey(key) || seen.has(key)) continue
        seen.add(key)
        const def = getEmployeeColumn(key)
        out.push({ key, visible: def.lockedVisible ? true : (item as { visible?: unknown }).visible !== false })
    }
    for (const def of EMPLOYEE_COLUMNS) {
        if (!seen.has(def.key)) out.push({ key: def.key, visible: def.defaultVisible })
    }
    return out
}

export interface ResolvedEmployeeSettings {
    columns: EmployeeColumnConfig[]
    sortKey: EmployeeColumnKey
    sortDir: SortDir
    showTotals: boolean
    showBars: boolean
    rowLimit: number
}

/** Every field filled in, whatever the saved layout held. */
export function resolveEmployeeSettings(s: EmployeePerformanceSettings | undefined | null): ResolvedEmployeeSettings {
    const columns = normalizeEmployeeColumns(s?.employeeColumns)
    const savedKeyUsable = isEmployeeColumnKey(s?.employeeSortKey) && getEmployeeColumn(s!.employeeSortKey!).sortable
    const sortKey = savedKeyUsable ? s!.employeeSortKey! : DEFAULT_EMPLOYEE_SORT.key
    // A saved direction belongs to the saved column: when that column is unknown or can't sort, the
    // fallback column takes its own default direction rather than inheriting a stranger's.
    const sortDir: SortDir = savedKeyUsable && (s?.employeeSortDir === 'asc' || s?.employeeSortDir === 'desc')
        ? s.employeeSortDir
        : (sortKey === DEFAULT_EMPLOYEE_SORT.key ? DEFAULT_EMPLOYEE_SORT.dir : getEmployeeColumn(sortKey).defaultDir)
    const limit = Number(s?.employeeRowLimit)
    return {
        columns,
        sortKey,
        sortDir,
        showTotals: s?.employeeShowTotals !== false,
        showBars: s?.employeeShowBars !== false,
        rowLimit: Number.isInteger(limit) && limit > 0 ? limit : 0,
    }
}

/** The settings patch that stores a resolved configuration (plain JSON — safe for layout saves). */
export function toEmployeeSettingsPatch(r: ResolvedEmployeeSettings): Required<EmployeePerformanceSettings> {
    return {
        employeeColumns: r.columns.map(c => ({ key: c.key, visible: c.visible })),
        employeeSortKey: r.sortKey,
        employeeSortDir: r.sortDir,
        employeeShowTotals: r.showTotals,
        employeeShowBars: r.showBars,
        employeeRowLimit: r.rowLimit,
    }
}

export function visibleEmployeeColumns(columns: EmployeeColumnConfig[]): EmployeeColumnDef[] {
    return columns.filter(c => c.visible).map(c => getEmployeeColumn(c.key))
}

/** Whether the widget shows the Manual grants column — the pages request those figures only then. */
export function employeeWantsManualGrants(settings: EmployeePerformanceSettings | undefined | null): boolean {
    return resolveEmployeeSettings(settings).columns.some(c => c.key === 'manualGrants' && c.visible)
}

/** Moves the column at `from` to index `to` (both clamped); returns a new array. */
export function moveEmployeeColumn(columns: EmployeeColumnConfig[], from: number, to: number): EmployeeColumnConfig[] {
    if (from < 0 || from >= columns.length) return columns.slice()
    const target = Math.max(0, Math.min(columns.length - 1, to))
    const next = columns.slice()
    const [item] = next.splice(from, 1)
    next.splice(target, 0, item)
    return next
}

/** Shows/hides a column; a column that can't be hidden is left as it is. */
export function setEmployeeColumnVisible(columns: EmployeeColumnConfig[], key: EmployeeColumnKey, visible: boolean): EmployeeColumnConfig[] {
    if (getEmployeeColumn(key).lockedVisible) return columns.slice()
    return columns.map(c => (c.key === key ? { ...c, visible } : c))
}

/** Header-click / sort-toggle semantics: the active column flips, any other column starts at its default. */
export function nextEmployeeSort(current: { key: EmployeeColumnKey; dir: SortDir }, key: EmployeeColumnKey): { key: EmployeeColumnKey; dir: SortDir } {
    if (!getEmployeeColumn(key).sortable) return current
    if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    return { key, dir: getEmployeeColumn(key).defaultDir }
}

// ── Values ───────────────────────────────────────────────────────────

const ratio = (num: number, den: number) => (den > 0 ? num / den : 0)

export function discountDepth(f: Pick<EmployeePerformanceFigures, 'netSales' | 'discounts'>): number {
    return ratio(f.discounts, f.netSales + f.discounts)
}

/** The numeric value a column shows for a set of figures (a row or the totals), or null for text columns. */
export function employeeColumnValue(key: EmployeeColumnKey, f: EmployeePerformanceFigures, totals: EmployeePerformanceFigures): number | null {
    switch (key) {
        case 'sales': return f.netSales
        case 'discounts': return f.discounts
        case 'bucks': return f.bucksIssued
        // Not requested (or an older server): no number, so the cell shows a dash rather than $0.00.
        case 'manualGrants': return f.manualGrants ?? null
        case 'discountPct': return discountDepth(f)
        case 'transactions': return f.transactions
        case 'avgSale': return ratio(f.netSales, f.transactions)
        case 'units': return f.units
        case 'upt': return ratio(f.units, f.transactions)
        case 'salesShare': return totals.netSales !== 0 ? f.netSales / totals.netSales : 0
        default: return null
    }
}

const currencyCents = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const currencyWhole = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })
const intFmt = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 })
const decimalFmt = new Intl.NumberFormat('en-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const percentFmt = new Intl.NumberFormat('en-CA', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 })

export function formatEmployeeValue(format: EmployeeColumnFormat, value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—'
    switch (format) {
        case 'currency': return currencyWhole.format(value)
        case 'currencyCents': return currencyCents.format(value)
        case 'int': return intFmt.format(value)
        case 'decimal': return decimalFmt.format(value)
        case 'percent': return percentFmt.format(value)
        default: return String(value)
    }
}

// ── Sorting ──────────────────────────────────────────────────────────

const nameCollator = new Intl.Collator('en-CA', { sensitivity: 'base', numeric: true })

/**
 * Sorts a copy of the rows. Ties (and the text column) fall back to name, then employeeId, so the
 * order is stable across refreshes and never depends on the server's row order. A figure the
 * server didn't send sorts as zero.
 */
export function sortEmployees(
    rows: readonly EmployeePerformanceRow[],
    key: EmployeeColumnKey,
    dir: SortDir,
    totals: EmployeePerformanceFigures,
): EmployeePerformanceRow[] {
    const sign = dir === 'asc' ? 1 : -1
    const byName = (a: EmployeePerformanceRow, b: EmployeePerformanceRow) =>
        nameCollator.compare(a.name, b.name) || (a.employeeId < b.employeeId ? -1 : a.employeeId > b.employeeId ? 1 : 0)
    return rows.slice().sort((a, b) => {
        if (key === 'employee' || key === 'rank') return sign * byName(a, b)
        const va = employeeColumnValue(key, a, totals) ?? 0
        const vb = employeeColumnValue(key, b, totals) ?? 0
        if (va !== vb) return sign * (va < vb ? -1 : 1)
        return byName(a, b)
    })
}

/** Initials for the avatar chip: first letters of the first and last words ("Mary-Ann van Dyke" → "MD"). */
export function employeeInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(w => /[\p{L}\p{N}]/u.test(w))
    if (words.length === 0) return '?'
    const first = words[0].match(/[\p{L}\p{N}]/u)?.[0] ?? ''
    const last = words.length > 1 ? (words[words.length - 1].match(/[\p{L}\p{N}]/u)?.[0] ?? '') : ''
    return (first + last).toUpperCase() || '?'
}
